import {
  ApiClient,
  ApiError,
  getMe,
  getMeMock,
  isMeFixture,
  PERMISSION_REFUSAL_CODES,
  refusePermissionIfInjected,
  setPermissionRefusalMock,
  setMeMockError,
  setMeMockFixture,
  setRoleAssignmentsReadMockError,
  searchUsersByPhone,
  searchUsersByPhoneMock,
  fetchUserDevice as fetchUserDeviceReal,
  fetchUserDeviceMock,
  fetchUserDeliveries as fetchUserDeliveriesReal,
  fetchUserDeliveriesMock,
  deleteUser,
  deleteUserMock,
  updateUser,
  updateUserMock,
  blockUser,
  blockUserMock,
  unblockUser,
  unblockUserMock,
  fetchDeliveryDetail as fetchDeliveryDetailReal,
  fetchDeliveryDetailMock,
  confirmDeliveryCollect as confirmDeliveryCollectReal,
  confirmDeliveryCollectMock,
  enableDelivery as enableDeliveryReal,
  enableDeliveryMock,
  nonCollectDelivery as nonCollectDeliveryReal,
  nonCollectDeliveryMock,
  rejectDelivery as rejectDeliveryReal,
  rejectDeliveryMock,
  retrieveDelivery as retrieveDeliveryReal,
  retrieveDeliveryMock,
  nonRetrieveDelivery as nonRetrieveDeliveryReal,
  nonRetrieveDeliveryMock,
  extendCustody as extendCustodyReal,
  extendCustodyMock,
  changeDeliveryRecipient as changeDeliveryRecipientReal,
  changeDeliveryRecipientMock,
  type RecipientChange,
  fetchLockerLog as fetchLockerLogReal,
  fetchLockerLogMock,
  fetchBoxStatus as fetchBoxStatusReal,
  fetchBoxStatusMock,
  fetchBoxMarks as fetchBoxMarksReal,
  fetchBoxMarksMock,
  clearBoxMarks as clearBoxMarksReal,
  clearBoxMarksMock,
  fetchLockCodes as fetchLockCodesReal,
  fetchLockCodesMock,
  confirmLockSequence as confirmLockSequenceReal,
  confirmLockSequenceMock,
  fetchLocations as fetchLocationsReal,
  fetchLocationsMock,
  searchDeliveriesByTracking as searchDeliveriesByTrackingReal,
  searchDeliveriesByTrackingMock,
  fetchLocationCarriers as fetchLocationCarriersReal,
  fetchLocationCarriersMock,
  fetchDeliveryStates as fetchDeliveryStatesReal,
  fetchDeliveryStatesMock,
  fetchLocationDeliveries as fetchLocationDeliveriesReal,
  fetchLocationDeliveriesMock,
  fetchDoorAccessCount as fetchDoorAccessCountReal,
  fetchDoorAccessCountMock,
  fetchDoorAccessLocation as fetchDoorAccessLocationReal,
  fetchDoorAccessLocationMock,
  setDoorAccessForLocations as setDoorAccessForLocationsReal,
  setDoorAccessForLocationsMock,
  fetchExclusionListUsers as fetchExclusionListUsersReal,
  fetchExclusionListUsersMock,
  addUserToExclusionList as addUserToExclusionListReal,
  addUserToExclusionListMock,
  removeUserFromExclusionList as removeUserFromExclusionListReal,
  removeUserFromExclusionListMock,
  fetchRoleAssignments as fetchRoleAssignmentsReal,
  fetchRoleAssignmentsMock,
  upsertRoleAssignment as upsertRoleAssignmentReal,
  upsertRoleAssignmentMock,
  removeAllRoleAssignment as removeAllRoleAssignmentReal,
  removeAllRoleAssignmentMock,
  type BoxStatus,
  type BoxMarks,
  type ConfirmLockSequenceInput,
  type LockCodes,
  type MeResponse,
  type UserSearchHit,
  type UserUpdate,
  type UserDevice,
  type UserDeliveriesResponse,
  type DeliveryDetail,
  type LockerLog,
  type LocationMatch,
  type TrackingSearchResponse,
  type CarrierOption,
  type AmazonDoorAccessLocation,
  type LocationDeliveriesQuery,
  type LocationDeliveriesResponse,
  type RoleAssignmentsResponse,
  type RoleAssignmentWrite,
  type PermissionRefusal,
} from '@citibox-panel/api-client';
import { getIdToken } from '@citibox-panel/auth';
import type {
  AddressMismatchCheck,
  AddressMismatchOutcome,
  AddressMismatchReport,
} from '@citibox-panel/lockers-care';
import type { AddExcludedUserOutcome, ExcludedUser } from '@citibox-panel/lockers-operations';
import type { Permission } from './access';
import { hasGoogleAccessToken } from './googleToken';
import { createFetchMe } from './me';
import { INERT_MOCK_CONTROLS, readMockControls, type MockControls } from './mockControls';
import { readRuntimeConfig, type RuntimeAppConfig } from './runtimeConfig';
import type { MismatchSheet } from './sheetsClient';

// Fail-safe: never default to production. An env that forgot API_BASE_URL gets an
// empty base (real calls fail same-origin) instead of silently talking to prod.
// Mock is the default when the flag is unset, so a forgotten env mocks rather
// than making unintended real calls.
export function resolveApiConfig(config: RuntimeAppConfig): {
  baseUrl: string;
  useMock: boolean;
} {
  return { baseUrl: config.API_BASE_URL ?? '', useMock: config.USE_MOCK_API !== 'false' };
}

const runtimeConfig = readRuntimeConfig();
const { baseUrl, useMock } = resolveApiConfig(runtimeConfig);

// Exported so the chrome can SAY so. Deliberately this binding and not a second
// `resolveApiConfig(readRuntimeConfig())` call at the call site: a banner that re-derived the flag
// could disagree with the client it describes, which would be a second lie on top of the first.
export const isMockMode = useMock;
if (!useMock && baseUrl === '') {
  console.warn(
    '[api] config.missing — real API calls need API_BASE_URL; refusing to default to production',
  );
}

// The URL is read only in mock mode: against the real backend these parameters are never parsed.
const mockControls =
  useMock && typeof window !== 'undefined'
    ? readMockControls(window.location.search)
    : INERT_MOCK_CONTROLS;

let meAfterFirstRead: MockControls['meAfterFirstRead'] = mockControls.meAfterFirstRead;
let meMockReads = 0;

// Lets a dev reproduce the re-read after a refusal: /me answers its fixture once, then what the
// control asked for.
export function applyMeScript(next: MockControls['meAfterFirstRead']): void {
  meAfterFirstRead = next;
  meMockReads = 0;
}

async function fetchMeMock(): Promise<MeResponse> {
  const me = await getMeMock();
  meMockReads += 1;
  if (meMockReads === 1 && meAfterFirstRead !== null) {
    if (meAfterFirstRead === 'error') setMeMockError('me_unavailable');
    else setMeMockFixture(meAfterFirstRead);
  }
  return me;
}

if (useMock) setPermissionRefusalMock(mockControls.refuse);

const [SHARED_REFUSAL_CODE, ACCESS_MANAGE_REFUSAL_CODE] = PERMISSION_REFUSAL_CODES;

// The access-management resource keeps the code it was delivered with; every other route answers
// the shared one.
function refusalCodeFor(permission: Permission): PermissionRefusal['code'] {
  return permission === 'access.manage' ? ACCESS_MANAGE_REFUSAL_CODE : SHARED_REFUSAL_CODE;
}

// Every mocked app-service call passes here, so one switch can make any of them answer a Rechazo
// por permiso for the permission of the view that calls it, with the code that permission's route
// answers.
async function viaMock<R>(call: string, permission: Permission, run: () => Promise<R>): Promise<R> {
  refusePermissionIfInjected(call, permission, refusalCodeFor(permission));
  return run();
}

export const apiClient = new ApiClient({
  baseUrl,
  getToken: () => getIdToken(),
});

const fetchMeReal = createFetchMe({
  read: () => getMe(apiClient),
  refreshToken: () => getIdToken(true),
});

// Lets a local dev server show another person's spaces; unknown names keep the default. Seeded into
// the mock rather than passed per read, so a ?mockMeAfter= script can still swap it after the first.
if (useMock && isMeFixture(runtimeConfig.MOCK_ME_FIXTURE)) {
  setMeMockFixture(runtimeConfig.MOCK_ME_FIXTURE);
}

// The one refusal a dev server can force: «Roles y permisos» denied mid-session, for screenshots.
if (useMock && runtimeConfig.MOCK_ROLES_READ_ERROR === 'access_manage_required') {
  setRoleAssignmentsReadMockError('access_manage_required');
}

export async function fetchMe(): Promise<MeResponse> {
  if (useMock) {
    return fetchMeMock();
  }
  return fetchMeReal();
}

// The «Roles y permisos» read: every role with its permissions, the permission catalog, and every
// Asignación in this environment. Only someone holding `access.manage` gets an answer; the server
// is the gate, the route guard is only how the screen says so.
//
// The resource is delivered ([AS-077], CBX-11681): the real branch reads it and the shape is
// parsed at the boundary, so a payload that drifts fails the read instead of granting anything.
export async function fetchRoleAssignments(): Promise<RoleAssignmentsResponse> {
  if (useMock) {
    return viaMock('fetchRoleAssignments', 'access.manage', () => fetchRoleAssignmentsMock());
  }
  return fetchRoleAssignmentsReal(apiClient);
}

// Create or replace one person's Asignación. The version read with the table travels with the
// write, so a change someone else made in between is refused (409) instead of silently overwritten;
// `null` means «this person has no Asignación yet».
export async function upsertRoleAssignment(write: RoleAssignmentWrite): Promise<void> {
  if (useMock) {
    return viaMock('upsertRoleAssignment', 'access.manage', () => upsertRoleAssignmentMock(write));
  }
  return upsertRoleAssignmentReal(apiClient, write);
}

// «Quitar todo»: an explicit removal of the whole Asignación, never an empty upsert, so emptying a
// person's access is always a deliberate act. It carries the version for the same reason the upsert
// does.
export async function removeAllRoleAssignment(email: string, expectedVersion: string): Promise<void> {
  if (useMock) {
    return viaMock('removeAllRoleAssignment', 'access.manage', () =>
      removeAllRoleAssignmentMock(email, expectedVersion),
    );
  }
  return removeAllRoleAssignmentReal(apiClient, email, expectedVersion);
}

export async function fetchUsersByPhone(phone: string): Promise<UserSearchHit[]> {
  if (useMock) {
    return viaMock('fetchUsersByPhone', 'care.search', () => searchUsersByPhoneMock(phone));
  }
  return searchUsersByPhone(apiClient, phone);
}

export async function fetchUserDevice(id: string): Promise<UserDevice | null> {
  if (useMock) {
    return viaMock('fetchUserDevice', 'care.search', () => fetchUserDeviceMock(id));
  }
  return fetchUserDeviceReal(apiClient, id);
}

export async function fetchUserDeliveries(id: string): Promise<UserDeliveriesResponse> {
  if (useMock) {
    return viaMock('fetchUserDeliveries', 'care.search', () => fetchUserDeliveriesMock(id));
  }
  return fetchUserDeliveriesReal(apiClient, id);
}

// The «Dar de baja» write, the first on the «Usuario» entity («Editar datos» below is the second).
// Its panel-context endpoint is live — CBX-11261 merged and deployed on 2026-09-03 — so the real
// branch is the one every deployed environment takes and the mock below is the dev/test path. The
// exclusion flag rides this same call rather than a second one, because the contract requires the
// two to succeed or fail together — no partial baja for a caller to unwind. A courier account's
// baja also carries the agent's reason; without one, nothing extra is passed on.
export async function deleteUserAccount(
  id: string,
  amazonExclusion: boolean,
  reason?: string,
): Promise<void> {
  if (useMock) {
    return viaMock('deleteUserAccount', 'care.search', () =>
      reason === undefined
        ? deleteUserMock(id, amazonExclusion)
        : deleteUserMock(id, amazonExclusion, reason),
    );
  }
  return reason === undefined
    ? deleteUser(apiClient, id, amazonExclusion)
    : deleteUser(apiClient, id, amazonExclusion, reason);
}

// The «Editar datos» write, the second on the «Usuario» entity. Its endpoint is live: [AS-055] 🟢
// since 2026-09-10, CBX-11384 deployed and verified against dev on 2026-09-14, so the real branch is
// the one every deployed environment takes and the mock below is the dev/test path only. One call
// for the three fields, never one per field, and the body carries only what changed: what the agent
// did not touch cannot be overwritten by this write.
export async function updateUserAccount(id: string, changes: UserUpdate): Promise<void> {
  if (useMock) {
    return viaMock('updateUserAccount', 'care.search', () => updateUserMock(id, changes));
  }
  return updateUser(apiClient, id, changes);
}

// The «Bloquear» write, the third on the «Usuario» entity, and the one that changes the account
// state and nothing else: no field is anonymized, no data is erased, and the «Entregas» stay
// readable. On a «Usuario» the id is the whole request, with no body; a courier account's block
// also carries the agent's reason.
//
// ⚠️ Mock-first: [AS-058] is 🟡 assumed and CBX-11441 has not delivered the contract, so the route
// below is what the panel PROPOSED, not what app-service confirmed. Until it lands, the real branch
// fails and the agent gets the floating notice.
export async function blockUserAccount(id: string, reason?: string): Promise<void> {
  if (useMock) {
    return viaMock('blockUserAccount', 'care.search', () =>
      reason === undefined ? blockUserMock(id) : blockUserMock(id, reason),
    );
  }
  return reason === undefined ? blockUser(apiClient, id) : blockUser(apiClient, id, reason);
}

// The «Desbloquear» write, offered only on a courier account, with the agent's reason. Its endpoint
// is delivered (CBX-11598) but not deployed yet, so the mock stands in for it until then; meanwhile
// the real branch fails and the agent gets the floating notice.
export async function unblockUserAccount(id: string, reason: string): Promise<void> {
  if (useMock) {
    return viaMock('unblockUserAccount', 'care.search', () => unblockUserMock(id, reason));
  }
  return unblockUser(apiClient, id, reason);
}

export async function fetchDeliveryDetail(id: string): Promise<DeliveryDetail> {
  if (useMock) {
    return viaMock('fetchDeliveryDetail', 'care.search', () => fetchDeliveryDetailMock(id));
  }
  return fetchDeliveryDetailReal(apiClient, id);
}

export async function confirmDeliveryCollect(id: string): Promise<void> {
  if (useMock) {
    return viaMock('confirmDeliveryCollect', 'care.search', () => confirmDeliveryCollectMock(id));
  }
  return confirmDeliveryCollectReal(apiClient, id);
}

export async function enableDelivery(id: string): Promise<void> {
  if (useMock) {
    return viaMock('enableDelivery', 'care.search', () => enableDeliveryMock(id));
  }
  return enableDeliveryReal(apiClient, id);
}

export async function nonCollectDelivery(id: string): Promise<void> {
  if (useMock) {
    return viaMock('nonCollectDelivery', 'care.search', () => nonCollectDeliveryMock(id));
  }
  return nonCollectDeliveryReal(apiClient, id);
}

export async function rejectDelivery(id: string): Promise<void> {
  if (useMock) {
    return viaMock('rejectDelivery', 'care.search', () => rejectDeliveryMock(id));
  }
  return rejectDeliveryReal(apiClient, id);
}

export async function retrieveDelivery(id: string): Promise<void> {
  if (useMock) {
    return viaMock('retrieveDelivery', 'care.search', () => retrieveDeliveryMock(id));
  }
  return retrieveDeliveryReal(apiClient, id);
}

export async function nonRetrieveDelivery(id: string): Promise<void> {
  if (useMock) {
    return viaMock('nonRetrieveDelivery', 'care.search', () => nonRetrieveDeliveryMock(id));
  }
  return nonRetrieveDeliveryReal(apiClient, id);
}

// The only write that carries a payload: the new expiration as a bare `YYYY-MM-DD` day.
export async function extendCustody(id: string, expirationDate: string): Promise<void> {
  if (useMock) {
    return viaMock('extendCustody', 'care.search', () => extendCustodyMock(id, expirationDate));
  }
  return extendCustodyReal(apiClient, id, expirationDate);
}

// Two ids and no personal data: the «Usuario» the parcel goes to, and the one the agent believed
// held it when they read the card. Mock-first while [AS-056] rides an app-service branch — the
// contract is delivered, not deployed.
export async function changeDeliveryRecipient(id: string, change: RecipientChange): Promise<void> {
  if (useMock) {
    return viaMock('changeDeliveryRecipient', 'care.search', () =>
      changeDeliveryRecipientMock(id, change),
    );
  }
  return changeDeliveryRecipientReal(apiClient, id, change);
}

// The box log is read BY BOX id (contract update 2026-07-27: `GET /boxes/<id>/logs`), not by
// delivery id — the caller resolves the box from the delivery detail's `box.id`.
export async function fetchLockerLog(boxId: string): Promise<LockerLog> {
  if (useMock) {
    return viaMock('fetchLockerLog', 'care.search', () => fetchLockerLogMock(boxId));
  }
  return fetchLockerLogReal(apiClient, boxId);
}

// Box-first like the log, but the delivery on screen rides along as the reference: the same «buzón»
// answers differently for two of its deliveries.
export async function fetchBoxStatus(boxId: string, deliveryId: string): Promise<BoxStatus> {
  if (useMock) {
    return viaMock('fetchBoxStatus', 'care.search', () => fetchBoxStatusMock(boxId, deliveryId));
  }
  return fetchBoxStatusReal(apiClient, boxId, deliveryId);
}

// Box-first like the log, and keyed by the box alone: the marks belong to the «buzón», so every
// delivery stored in it reads the same level.
export async function fetchBoxMarks(boxId: string): Promise<BoxMarks> {
  if (useMock) {
    return viaMock('fetchBoxMarks', 'care.search', () => fetchBoxMarksMock(boxId));
  }
  return fetchBoxMarksReal(apiClient, boxId);
}

// The panel-context endpoint is live (CBX-11149), so production takes the real branch — `useMock`
// is the dev/test path, never a per-feature switch: it is one global runtime flag and it is
// `false` in every deployed environment.
export async function clearBoxMarks(boxId: string): Promise<void> {
  if (useMock) {
    return viaMock('clearBoxMarks', 'care.search', () => clearBoxMarksMock(boxId));
  }
  return clearBoxMarksReal(apiClient, boxId);
}

// LOCK-first, unlike the log and the status beside it: the code sequences belong to the cerradura
// (its devices-service id, `lock.id` on the detail), not to the «buzón» it is paired with.
export async function fetchLockCodes(lockId: string): Promise<LockCodes> {
  if (useMock) {
    return viaMock('fetchLockCodes', 'care.search', () => fetchLockCodesMock(lockId));
  }
  return fetchLockCodesReal(apiClient, lockId);
}

// LOCK-first like the read above, and keyed the same way. CBX-10989 is live too, so this takes the
// real branch in production for the same reason the clear above does.
export async function confirmLockSequence(
  lockId: string,
  input: ConfirmLockSequenceInput,
): Promise<void> {
  if (useMock) {
    return viaMock('confirmLockSequence', 'care.search', () =>
      confirmLockSequenceMock(lockId, input),
    );
  }
  return confirmLockSequenceReal(apiClient, lockId, input);
}

// The three «Dirección» reads, wired to app-service like every fetcher above them. The term reaches
// the search already trimmed — the server trims before validating its 3-character floor, so the
// panel measures the same string it sends.
export async function fetchLocations(query: string): Promise<LocationMatch[]> {
  if (useMock) {
    return viaMock('fetchLocations', 'care.search', () => fetchLocationsMock(query));
  }
  return fetchLocationsReal(apiClient, query);
}

// The «Tracking» read. It was wired and deployed visible while the endpoint was still only a
// request, because the alternative — hiding the search until it landed — makes the whole feature
// dark. What that costs is a rule the caller cannot dodge: a REJECTED read and a tracking that
// matched nothing are different outcomes and must be worded differently, or the panel tells an
// agent holding a parcel that it was never stored.
//
// ⛔ Backend delivered on 2026-09-09, so the real branch no longer answers 404 to everything — and
// `useMock` is global, `false` in every deployed environment. The real branch below still CASTS its
// payload instead of parsing it (see the endpoint's own note): mirroring the delivered shape is a
// PRE-MERGE task now, because there is no per-feature gate between this merge and CC.
export async function searchDeliveriesByTracking(
  tracking: string,
): Promise<TrackingSearchResponse> {
  if (useMock) {
    return viaMock('searchDeliveriesByTracking', 'care.search', () =>
      searchDeliveriesByTrackingMock(tracking),
    );
  }
  return searchDeliveriesByTrackingReal(apiClient, tracking);
}

// Delivered (AS-041, CBX-11167). A payload the schema rejects still degrades the filter to «Todos
// los carriers» rather than half-rendering it — the designed fail-open, not a backstop.
export async function fetchLocationCarriers(locationId: number): Promise<CarrierOption[]> {
  if (useMock) {
    return viaMock('fetchLocationCarriers', 'care.search', () =>
      fetchLocationCarriersMock(locationId),
    );
  }
  return fetchLocationCarriersReal(apiClient, locationId);
}

// Global, not per building: what states EXIST does not depend on which one is on screen, which is
// why this fetcher takes nothing and its cache key names no location.
export async function fetchDeliveryStates(): Promise<string[]> {
  if (useMock) {
    return viaMock('fetchDeliveryStates', 'care.search', () => fetchDeliveryStatesMock());
  }
  return fetchDeliveryStatesReal(apiClient);
}

// The location id is the INTEGER the read knows, unwrapped from the shell's `loc:<id>` tab key by
// the caller — the tab key is a string and the query is a number, and that boundary is explicit.
export async function fetchLocationDeliveries(
  locationId: number,
  query: LocationDeliveriesQuery,
): Promise<LocationDeliveriesResponse> {
  if (useMock) {
    return viaMock('fetchLocationDeliveries', 'care.search', () =>
      fetchLocationDeliveriesMock(locationId, query),
    );
  }
  return fetchLocationDeliveriesReal(apiClient, locationId, query);
}

// How many locations sit on one side of the Amazon door-access gate. Returns the COUNT: the whole
// filtered table travels and the screen reads one integer off it, so the rows never reach the cache.
export async function fetchDoorAccessCount(enabled: boolean): Promise<number> {
  if (useMock) {
    return viaMock('fetchDoorAccessCount', 'operations.amazon_door_access', () =>
      fetchDoorAccessCountMock(enabled),
    );
  }
  return fetchDoorAccessCountReal(apiClient, enabled);
}

// The state of one location at the Amazon door-access gate. Never 404s: an unconfigured id answers
// the default state, so the caller has no «not found» branch to write.
export async function fetchDoorAccessLocation(
  locationId: number,
): Promise<AmazonDoorAccessLocation> {
  if (useMock) {
    return viaMock('fetchDoorAccessLocation', 'operations.amazon_door_access', () =>
      fetchDoorAccessLocationMock(locationId),
    );
  }
  return fetchDoorAccessLocationReal(apiClient, locationId);
}

// Move a batch of locations to one side of the Amazon door-access gate. One request, never chunked:
// one request is one outcome, and that is what makes the counter delta on the card interpretable.
export async function setDoorAccessForLocations(
  locationIds: number[],
  enabled: boolean,
): Promise<void> {
  if (useMock) {
    return viaMock('setDoorAccessForLocations', 'operations.amazon_door_access', () =>
      setDoorAccessForLocationsMock(locationIds, enabled),
    );
  }
  return setDoorAccessForLocationsReal(apiClient, locationIds, enabled);
}

// The three seams of the carrier exclusion lists, keyed by the carrier's `courier.slug`, which the
// host's catalog hands down — a constant, never input. The endpoints are delivered and deployed
// (app-service CBX-11660), so production takes the real branch; `useMock` is the dev/test path.
export async function fetchExcludedUsers(slug: string): Promise<ExcludedUser[]> {
  if (useMock) {
    return viaMock('fetchExcludedUsers', 'operations.carrier_exclusion_lists', () =>
      fetchExclusionListUsersMock(slug),
    );
  }
  return fetchExclusionListUsersReal(apiClient, slug);
}

// A phone no live account holds is the normal answer to a mistyped number, so it comes back as an
// OUTCOME the card words in the field; anything else stays a failure for the fixed-copy alert. The
// mapping lives here, at the seam, so the module never sees `ApiError` or a wire code.
export async function addExcludedUser(slug: string, phone: string): Promise<AddExcludedUserOutcome> {
  try {
    if (useMock) {
      await viaMock('addExcludedUser', 'operations.carrier_exclusion_lists', () =>
        addUserToExclusionListMock(slug, phone),
      );
    } else {
      await addUserToExclusionListReal(apiClient, slug, phone);
    }
    return { kind: 'added' };
  } catch (error) {
    if (error instanceof ApiError && error.code === 'unregistered_user') {
      return { kind: 'unregistered_phone' };
    }
    throw error;
  }
}

export async function removeExcludedUser(slug: string, userId: number): Promise<void> {
  if (useMock) {
    return viaMock('removeExcludedUser', 'operations.carrier_exclusion_lists', () =>
      removeUserFromExclusionListMock(slug, userId),
    );
  }
  return removeUserFromExclusionListReal(apiClient, slug, userId);
}

// «Notificar mismatch» writes to a Google Sheet shared with Amazon, not to app-service, so its
// target comes from its own runtime keys — and so does whether the send is REAL: a configured sheet
// is written to whatever USE_MOCK_API says, which is what lets the Google path be tried from a dev
// panel that still mocks the backend. The range falls back to the first tab's four columns.
//
// With no sheet configured, nothing reaches Google: against the real backend the send answers
// SHEETS_NOT_FOUND (the «tell whoever maintains the integration» notice), and in mock mode a
// synthetic row (nothing leaves the browser), so the flow is walkable in dev. The button itself is
// offered everywhere (product decision, 2026-09-23) — no environment writes to a sheet it was not
// given, but none hides the action either.
const mismatchSheet: MismatchSheet | null =
  runtimeConfig.GOOGLE_OAUTH_CLIENT_ID !== undefined &&
  runtimeConfig.MISMATCH_SPREADSHEET_ID !== undefined
    ? {
        clientId: runtimeConfig.GOOGLE_OAUTH_CLIENT_ID,
        spreadsheetId: runtimeConfig.MISMATCH_SPREADSHEET_ID,
        range: runtimeConfig.MISMATCH_SHEET_RANGE ?? 'A:D',
      }
    : null;

// The synthetic send never reaches Google: it answers a made-up row.
const MOCK_APPENDED_ROW = 2;

export async function reportAddressMismatch(
  report: AddressMismatchReport,
): Promise<AddressMismatchOutcome> {
  if (mismatchSheet !== null) {
    // Loaded by the first send, not with this module: most sessions never report, so the Sheets
    // transport (and the Google token client behind it) stays out of the main chunk.
    const { reportAddressMismatchToSheet } = await import('./sheetsClient');
    return reportAddressMismatchToSheet(mismatchSheet, report);
  }
  if (useMock) {
    return { kind: 'appended', row: MOCK_APPENDED_ROW };
  }
  throw new ApiError(0, 'SHEETS_NOT_FOUND', null, 'No mismatch sheet is configured');
}

// Whether a tracking is already in the sheet, asked as the «Entrega» opens. Only a configured sheet
// AND a Google token already in memory can answer; otherwise «not reported», and the button shows.
// The token is checked first so that opening an «Entrega» never loads the Sheets client on its own:
// a token in memory means a send already loaded it.
export async function checkAddressMismatchReport(tracking: string): Promise<AddressMismatchCheck> {
  if (mismatchSheet === null || !hasGoogleAccessToken()) return { reported: false };
  const { findAddressMismatchReport } = await import('./sheetsClient');
  return findAddressMismatchReport(mismatchSheet, tracking);
}
